/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from "@solana/kit";

export const STAKING_CONFIG_DISCRIMINATOR = new Uint8Array([
  45, 134, 252, 82, 37, 57, 84, 25,
]);

export function getStakingConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    STAKING_CONFIG_DISCRIMINATOR,
  );
}

export type StakingConfig = {
  discriminator: ReadonlyUint8Array;
  /** Authority who can update staking parameters */
  authority: Address;
  /** Minimum stake amount (1,000 GHOST) */
  minStake: bigint;
  /** Minimum lock duration (30 days in seconds) */
  minLockDuration: bigint;
  /** Slash percentage for fraud (50% = 5000 bps) */
  fraudSlashBps: number;
  /** Slash percentage for dispute loss (10% = 1000 bps) */
  disputeSlashBps: number;
  /** Treasury account for slashed tokens */
  treasury: Address;
  bump: number;
};

export type StakingConfigArgs = {
  /** Authority who can update staking parameters */
  authority: Address;
  /** Minimum stake amount (1,000 GHOST) */
  minStake: number | bigint;
  /** Minimum lock duration (30 days in seconds) */
  minLockDuration: number | bigint;
  /** Slash percentage for fraud (50% = 5000 bps) */
  fraudSlashBps: number;
  /** Slash percentage for dispute loss (10% = 1000 bps) */
  disputeSlashBps: number;
  /** Treasury account for slashed tokens */
  treasury: Address;
  bump: number;
};

/** Gets the encoder for {@link StakingConfigArgs} account data. */
export function getStakingConfigEncoder(): FixedSizeEncoder<StakingConfigArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["authority", getAddressEncoder()],
      ["minStake", getU64Encoder()],
      ["minLockDuration", getI64Encoder()],
      ["fraudSlashBps", getU16Encoder()],
      ["disputeSlashBps", getU16Encoder()],
      ["treasury", getAddressEncoder()],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: STAKING_CONFIG_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link StakingConfig} account data. */
export function getStakingConfigDecoder(): FixedSizeDecoder<StakingConfig> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["authority", getAddressDecoder()],
    ["minStake", getU64Decoder()],
    ["minLockDuration", getI64Decoder()],
    ["fraudSlashBps", getU16Decoder()],
    ["disputeSlashBps", getU16Decoder()],
    ["treasury", getAddressDecoder()],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link StakingConfig} account data. */
export function getStakingConfigCodec(): FixedSizeCodec<
  StakingConfigArgs,
  StakingConfig
> {
  return combineCodec(getStakingConfigEncoder(), getStakingConfigDecoder());
}

export function decodeStakingConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<StakingConfig, TAddress>;
export function decodeStakingConfig<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<StakingConfig, TAddress>;
export function decodeStakingConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<StakingConfig, TAddress> | MaybeAccount<StakingConfig, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getStakingConfigDecoder(),
  );
}

export async function fetchStakingConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<StakingConfig, TAddress>> {
  const maybeAccount = await fetchMaybeStakingConfig(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeStakingConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<StakingConfig, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeStakingConfig(maybeAccount);
}

export async function fetchAllStakingConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<StakingConfig>[]> {
  const maybeAccounts = await fetchAllMaybeStakingConfig(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeStakingConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<StakingConfig>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeStakingConfig(maybeAccount));
}

export function getStakingConfigSize(): number {
  return 93;
}
